import { AssistantStatusIndicator } from 'src/components/assistant/AssistantStatusIndicator';
import { ChannelTypingIndicator } from 'src/features/channel/channel-content/ChannelTypingIndicator';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';

interface ChannelStatusIndicatorsProps {
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
    isCancelable: boolean;
  } | null;
  typingStatus?: {
    typingUsers: Array<{
      user?: {
        id?: string;
        name?: string;
      };
    }>;
  } | null;
  isInternalChat?: boolean;
  getCompanyFromId?: (companyId: string) => { name?: string } | null;
  getClientFromId?: (clientId: string) => { name?: string } | null;
  clearAIAssistantStatus?: () => void;
  cancelAIStream?: () => void;
  isChatUIEnabled?: boolean;
}

export const ChannelStatusIndicators = ({
  aiAssistantStatus,
  typingStatus,
  isInternalChat = false,
  getCompanyFromId,
  getClientFromId,
  clearAIAssistantStatus,
  cancelAIStream,
  isChatUIEnabled = false,
}: ChannelStatusIndicatorsProps) => {
  const { isSidebarTheme } = useMessagingTheme();
  const hasAIStatus = !!aiAssistantStatus;
  const hasTyping = !!typingStatus;

  if (!hasAIStatus && !hasTyping && !isInternalChat) {
    // For internal chat only we reserve the space for the status indicators
    // but for client-facing chat there is not ai status indicator so return null.
    return null;
  }

  const hasBoth = hasAIStatus && hasTyping;

  return (
    <div
      className={[
        `${isSidebarTheme ? 'px-4' : 'pl-4 pr-9'}`,
        `${hasAIStatus || hasTyping ? 'translate-y-0' : 'translate-y-[100%]'}`,
        !isChatUIEnabled
          ? ''
          : [
              'z-0',
              hasBoth ? 'mb-[-12px]' : 'mb-[-8px]',
              'transition-transform',
              'duration-100',
              'will-change-transform',
            ].join(' '),
        hasBoth ? 'h-20' : 'h-10',
      ].join(' ')}
    >
      {(hasAIStatus || hasTyping) && (
        <div className="flex flex-col bg-gray-150 rounded-t-lg px-3 py-1 gap-1 pb-3 z-10">
          {aiAssistantStatus && (
            <AssistantStatusIndicator
              status={aiAssistantStatus.status}
              metadata={aiAssistantStatus.metadata}
              getCompanyFromId={getCompanyFromId}
              getClientFromId={getClientFromId}
              onClearStatus={clearAIAssistantStatus}
              onCancelStream={cancelAIStream}
              isCancelable={aiAssistantStatus.isCancelable}
            />
          )}
          {typingStatus && (
            <ChannelTypingIndicator typingUsers={typingStatus.typingUsers} />
          )}
        </div>
      )}
    </div>
  );
};
